import { useEffect, useRef, useState } from "react";
import type { FC, RefObject } from "react";
import type { ModalProps } from "@uhg-harmony/react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { templatesActions } from "../../redux/templates/templatesSlice";
import { selectTemplatesLoading } from "../../redux/templates/templatesTreeSelectors";
import { useAuth } from "react-oidc-context";
import {
  BaseTemplateFormModal,
  type BaseTemplateFormData,
} from "./BaseTemplateFormModal";
import { S3DataConnector } from "../../api/s3DataConnector";
import { S3_CONSTANTS } from "../../constants/templateConstants";
import type { EditorRef } from "../Editor";
import { sendTemplateNotification } from "../../features/notifications/utils/sendTemplateNotification";
import { NOTIFICATION_TITLES } from "../../features/notifications/constants";
import { mapUICategoryToAPI } from "../utils/categoryMapping";
import { createLogger } from "../../features/notifications/utils/logger";
import {
  setTemplateHtmlById,
  selectAccountId,
  selectClientId,
} from "../../redux/metadata/metadataSlice";
import { buildRefreshParams } from "../TemplateBuilder/utils/buildRefreshParams";
import { normalizeHtmlPlaceholders } from "../utils/normalizeHtmlPlaceholders";

const logger = createLogger("SaveAsDraft");

type SaveAsDraftModalProps = ModalProps & {
  open: boolean;
  onClose: () => void;
  templateName?: string;
  editorRef?: RefObject<EditorRef | null>;
  templateVersionId?: string;
  templateId?: string;
  onSuccess?: (metadata?: {
    templateName: string;
    category: string;
    folderName: string;
    htmlContent: string;
    templateId: string;
    templateRowId: string;
    templateVersionId: number;
    platformId: number;
    clientId: number;
  }) => void;
};

export const SaveAsDraftModal: FC<SaveAsDraftModalProps> = ({
  open,
  onClose,
  templateName,
  editorRef,
  templateVersionId,
  templateId,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const auth = useAuth();

  // Get values from Redux
  const clientId = useAppSelector(selectClientId);
  const accountId = useAppSelector(selectAccountId);

  // Monitor Redux state for saga response
  const templatesState = useAppSelector((state) => state.templates);
  const isLoading = useAppSelector(selectTemplatesLoading);
  const lastOperation = useAppSelector(
    (state) => state.templates.lastOperation,
  );

  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [pendingFormData, setPendingFormData] =
    useState<BaseTemplateFormData | null>(null);
  const lastHandledDraftResultRef = useRef<string>("");

  // Clear error message and reset saving state when modal opens
  useEffect(() => {
    if (open) {
      setErrorMessage("");
      setIsSaving(false);
    }
  }, [open]);

  // Monitor Redux state for saga response and handle S3 upload
  useEffect(() => {
    const handleSagaResponse = async () => {
      // Only proceed if we have a createDraft operation result
      if (!isSaving || !lastOperation || lastOperation.name !== "createDraft") {
        return;
      }

      // Handle success case
      if (!isLoading && lastOperation.result && !templatesState.error) {
        try {
          const apiResult = lastOperation.result;

          if (
            !apiResult ||
            !apiResult.versions ||
            apiResult.versions.length === 0
          ) {
            throw new Error(
              "Invalid API response: missing version information",
            );
          }

          const latestVersion = apiResult.versions[0];
          const putPresignedUrl = latestVersion.putPresignedUrl;

          const draftResultKey = [
            String(apiResult.templateRowId ?? ""),
            String(latestVersion.templateVersionId ?? ""),
            String(putPresignedUrl ?? ""),
          ].join("|");
          if (
            draftResultKey &&
            draftResultKey === lastHandledDraftResultRef.current
          ) {
            logger.debug("[Save as Draft] Skipping duplicate draft result handling", {
              draftResultKey,
            });
            return;
          }
          lastHandledDraftResultRef.current = draftResultKey;

          if (!putPresignedUrl) {
            throw new Error("No presigned URL in API response");
          }

          // Get HTML content from editor
          const htmlContent = editorRef?.current?.getHtml();
          if (!htmlContent) {
            throw new Error("No content to save");
          }

          const processedHtmlContent = normalizeHtmlPlaceholders(htmlContent, { mode: "toToken" })

          const resolvedTemplateId = String(apiResult.templateId ?? "").trim();
          const templateIdVersion = resolvedTemplateId
            ? `${resolvedTemplateId}:Draft`
            : "";
          if (resolvedTemplateId) {
            dispatch(
              setTemplateHtmlById({
                templateId: templateIdVersion,
                html: processedHtmlContent,
              }),
            );
          }

          logger.debug("[Save as Draft] Uploading to S3:", {
            putPresignedUrl,
            htmlLength: processedHtmlContent.length,
            htmlPreview: processedHtmlContent.substring(0, 200),
          });

          // Upload HTML to S3
          const s3Connector = new S3DataConnector();
          await s3Connector.uploadFile(processedHtmlContent, putPresignedUrl, S3_CONSTANTS.PLATFORM_ID);
          logger.debug("[Save as Draft] S3 upload complete");

          

          // Publish notification via ECP notification system
          if (pendingFormData) {
            const notificationMessage = `${resolvedTemplateId ? `${resolvedTemplateId} - ` : ""}Draft Template Name - ${pendingFormData.templateName} saved successfully at Folder: ${pendingFormData.folderName}!`;

            sendTemplateNotification(
              auth.user?.access_token,
   
              NOTIFICATION_TITLES.TEMPLATE_DRAFT_SAVED,
              notificationMessage,
              pendingFormData.category,
                (err) => logger.error("Save as draft notification failed:", err),
            );

            // Call success callback with metadata
            onSuccess?.({
              templateName: pendingFormData.templateName,
              category: pendingFormData.category,
              folderName: pendingFormData.folderName,
              htmlContent: htmlContent,
              templateId: apiResult.templateId ?? "",
              templateRowId: String(apiResult.templateRowId ?? ""),
              templateVersionId:
                apiResult.versions?.[0]?.templateVersionId ?? 0,
              platformId: apiResult.platformId ?? 0,
              clientId: apiResult.clientId ?? 0,
            });
          }

          // Parent onSuccess in TemplateBuilder performs the authoritative
          // scope-aware refresh. Avoid dispatching another list request here,
          // because getTemplates uses takeLatest and the later request can
          // cancel the correct-scope refresh and leave the tree stale.
          if (!onSuccess && auth.user?.access_token) {
            dispatch(templatesActions.getTemplatesRequested(
              buildRefreshParams(auth.user.access_token, accountId, clientId)
            ));
          }

          // Close modal
          setIsSaving(false);
          setPendingFormData(null);
          onClose();
        } catch (error: unknown) {
          const displayMessage =
            (error as Error)?.message || "Failed to upload template to S3";
          setErrorMessage(`Failed to save draft: ${displayMessage}`);
          setIsSaving(false);
          setPendingFormData(null);
        }
      }

      // Handle failure case
      if (!isLoading && templatesState.error) {
        setErrorMessage(`Failed to save draft: ${templatesState.error}`);
        setIsSaving(false);
        setPendingFormData(null);
      }
    };

    handleSagaResponse();
  }, [
    isSaving,
    isLoading,
    lastOperation,
    templatesState.error,
    editorRef,
    pendingFormData,
    onSuccess,
    onClose,
    auth.user?.profile?.sub,
    auth.user?.access_token,
    accountId,
    clientId,
  ]);

  const handleConfirm = async (formData: BaseTemplateFormData) => {
    try {
      setIsSaving(true);
      setErrorMessage("");
      setPendingFormData(formData);
      lastHandledDraftResultRef.current = "";

      // Get HTML content from editor
      const htmlContent = editorRef?.current?.getHtml();

      if (!htmlContent) {
        setErrorMessage("No content to save");
        setIsSaving(false);
        return;
      }

      if (!auth.user?.access_token) {
        setErrorMessage("Authentication required");
        setIsSaving(false);
        return;
      }

      // Map category to TemplateCategory type
      const templateCategory = mapUICategoryToAPI(formData.category);

      const reqData = {
        token: auth.user.access_token,
        templateName: formData.templateName,
        templateFolder: formData.folderName,
        templateCategory,
        ...(templateCategory === "PLATFORM" || templateCategory === "CLIENT"
          ? { platformId: accountId || "" }
          : {}),
        ...(templateCategory === "CLIENT"
          ? { platformClientsId: clientId || "" }
          : {}),
        templateVersionId: templateVersionId || "",
        templateId: templateVersionId && templateId ? templateId : "",
      };

      logger.debug("[Save as Draft] Dispatching createDraftRequested:", {
        templateName: reqData.templateName,
        templateFolder: reqData.templateFolder,
        templateCategory: reqData.templateCategory,
        platformId: reqData.platformId,
        platformClientsId: reqData.platformClientsId,
        templateId: reqData.templateId,
        templateVersionId: reqData.templateVersionId,
      });

      dispatch(templatesActions.createDraftRequested(reqData));
    } catch (error: unknown) {
      setErrorMessage(
        `Failed to save draft: ${(error as Error)?.message || "Unknown error"}`,
      );
      setIsSaving(false);
      setPendingFormData(null);
    }
  };

  return (
    <BaseTemplateFormModal
      title="Save Template As Draft"
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      initialTemplateName={templateName}
      currentTemplateId={templateId}
      confirmButtonText="Save As Draft"
      isLoading={isSaving}
      errorMessage={errorMessage}
      showCategoryDropdown
      showFolderDropdown
      defaultFolderSelection="Drafts"
      isTemplateNameDisabled={!!templateId}
    />
  );
};
